import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, Button } from '../MUIs';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useFileUpload from '../../hooks/useFileUpload';
import Global from '../../Services/Common/Global';
import Commons from '../../Services/Common/Common';

/**
 * 다중 파일 업로드 모달 (최대 5개, 미리보기 지원)
 *
 * props:
 * - open: boolean
 * - title: string
 * - onClose: () => void
 * - onSave: (files) => void
 *   files: Array<{ fileName: string, previewFilePath: string }>
 * - initialFiles?: Array<{ fileName: string, previewFilePath: string }>
 */
function FileUploadMultiDialog({ open, title = '파일 첨부', onClose, onSave, initialFiles }) {
  const MAX_FILES = 5;
  const { SaveLogicPreview } = useFileUpload();

  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  /** data:/blob: URL은 그대로, 서버 상대 경로만 파일 API URL로 보완 */
  const ResolveItemPreviewSrc = useCallback((it) => {
    const direct = it?.previewUrl?.trim?.() || '';
    if (direct.startsWith('data:') || direct.startsWith('blob:')) return direct;
    if (direct) return direct;
    const path = it?.previewFilePath?.trim?.() || '';
    if (!path || path.startsWith('data:') || path.startsWith('blob:')) return path;
    return Global.file.useUrl + 'files/' + Commons.EncodeUrl(path);
  }, []);

  useEffect(() => {
    if (!open) return;
    const source = Array.isArray(initialFiles) ? initialFiles : [];
    const base =
      source.slice(0, MAX_FILES).map((f, idx) => ({
        id: idx,
        fileName: f.fileName || '',
        previewFilePath: f.previewFilePath || '',
        previewUrl: f.previewUrl || '',
      })) || [];
    while (base.length < MAX_FILES) {
      base.push({ id: base.length, fileName: '', previewFilePath: '', previewUrl: '' });
    }
    setItems(base);
  }, [open]);

  const handleFileChange = async (index, file) => {
    if (!file) return;

    // 1) 먼저 로컬에서 바로 미리보기(data URL) 생성 → 서버 업로드 실패해도 미리보기는 보이도록
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result || '';
      setItems((prev) =>
        prev.map((it, idx) =>
          idx === index
            ? {
                ...it,
                fileName: file.name,
                // previewFilePath는 나중에 서버 응답으로 채움
                previewUrl: dataUrl,
              }
            : it
        )
      );
    };
    reader.readAsDataURL(file);

    // 2) 비동기로 서버에 PreviewFiles 업로드 → 경로만 별도 보관
    try {
      const previewPath = await SaveLogicPreview(file);
      if (!previewPath) return;
      setItems((prev) =>
        prev.map((it, idx) =>
          idx === index
            ? {
                ...it,
                previewFilePath: previewPath,
              }
            : it
        )
      );
    } catch {
      // 서버 업로드 실패 시에도 미리보기는 유지
    }
  };

  const handleRemove = (index) => {
    setItems((prev) =>
      prev.map((it, idx) =>
        idx === index
          ? {
              ...it,
              fileName: '',
              previewFilePath: '',
              previewUrl: '',
            }
          : it
      )
    );
  };

  const handleSave = async () => {
    if (!onSave) {
      onClose?.();
      return;
    }
    setSaving(true);
    try {
      const filtered = items
        .filter((it) => it.fileName && it.previewFilePath)
        .map((it) => ({
          fileName: it.fileName,
          previewFilePath: it.previewFilePath,
          previewUrl: it.previewUrl,
        }));
      await onSave(filtered);
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      title={title}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      saveButtonName="저장"
      closeButtonName="닫기"
      useSaveButton
      useSaveAfterClose={false}
      onSave={handleSave}
      contentSx={{ padding: 2, maxHeight: '90vh' }}
      content={
        <Box
          // 다이어로그 전체 폭 기준으로 카드 5개를 일렬로 균등 배치
          display="grid"
          gridTemplateColumns="repeat(5, 1fr)"
          gap={2}
          sx={{
            // 내용 영역은 70vh까지 사용, 내부 스크롤
            maxHeight: '70vh',
            overflowY: 'auto',
          }}
        >
          {items.map((item, index) => {
            const previewSrc = ResolveItemPreviewSrc(item);
            return (
            <Box
              key={item.id}
              sx={{
                border: '1px solid #ddd',
                borderRadius: 1,
                padding: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: 200, // 카드 높이 확장
                  borderRadius: 1,
                  backgroundColor: '#fafafa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  position: 'relative',
                  border: '1px dashed #ddd',
                }}
              >
                {previewSrc ? (
                  <img
                    src={previewSrc}
                    alt={item.fileName}
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                ) : (
                  <Typography variant="caption" color="textSecondary">
                    미리보기 없음
                  </Typography>
                )}
              </Box>
              <input
                type="file"
                style={{ display: 'none' }}
                id={`file-upload-multi-input-${index}`}
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileChange(index, file);
                  e.target.value = '';
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  width: '100%',
                  mt: 1,
                }}
              >
                <label htmlFor={`file-upload-multi-input-${index}`} style={{ flex: 1 }}>
                  <Button
                    component="span"
                    size="small"
                    name={item.fileName ? '변경' : '파일 선택'}
                    disabled={saving}
                    sx={{ width: '100%' }}
                  />
                </label>
                <Button
                  size="small"
                  color="error"
                  disabled={!item.fileName || saving}
                  onClick={() => handleRemove(index)}
                  sx={{ minWidth: 70 }}
                  name="삭제"
                />
              </Box>
            </Box>
            );
          })}
        </Box>
      }
    />
  );
}

export default FileUploadMultiDialog;

